import React, { useEffect, useState, useMemo } from 'react';
import { transfersAPI, sitesAPI, suppliersAPI, supplierProductsAPI, industrialProductsAPI } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { ArrowRightLeft, ArrowRight, Plus, Check, X, ChevronDown, ChevronUp, Truck, MapPin, Package, Clock, CheckCircle2, XCircle, Trash2, Search, Pencil, FileText, Download, ArrowUpDown, Warehouse } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { formatCurrency, matchesSearch } from '@/lib/utils';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import PrintButton from '@/components/PrintButton';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
    pending: { label: 'En attente', color: 'bg-amber-100 text-amber-700 border-amber-300', icon: Clock },
    validated: { label: 'Valide', color: 'bg-emerald-100 text-emerald-700 border-emerald-300', icon: CheckCircle2 },
    rejected: { label: 'Rejete', color: 'bg-red-100 text-red-700 border-red-300', icon: XCircle },
};

function StatusBadge({ status }) {
    const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.pending;
    const Icon = cfg.icon;
    return (
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${cfg.color}`} data-testid={`status-badge-${status}`}>
            <Icon className="w-3 h-3" /> {cfg.label}
        </span>
    );
}

function ProductPicker({ value, onChange, groups, stock, allProducts, hasFromSite, testId }) {
    const [open, setOpen] = useState(false);
    const [q, setQ] = useState('');
    const selected = allProducts.find(p => p.id === value);
    const filtered = useMemo(() =>
        groups.map(([cat, list]) => [cat, list.filter(p => matchesSearch(p.name, q))]).filter(([, l]) => l.length),
        [groups, q]);
    return (
        <Popover modal open={open} onOpenChange={(o) => { setOpen(o); if (o) setQ(''); }}>
            <PopoverTrigger asChild>
                <button type="button"
                    className="flex-1 h-8 px-2 border rounded-md text-xs bg-white flex items-center justify-between gap-1 min-w-0 text-left"
                    data-testid={testId}>
                    <span className={`truncate ${selected ? 'text-slate-800' : 'text-slate-400'}`}>
                        {selected ? `${selected.name} (${selected.unit})` : '-- Produit --'}
                    </span>
                    <ChevronDown className="w-3 h-3 text-slate-400 flex-shrink-0" />
                </button>
            </PopoverTrigger>
            <PopoverContent className="p-0 w-[var(--radix-popover-trigger-width)] min-w-[260px]" align="start">
                <div className="p-2 border-b">
                    <div className="relative">
                        <Search className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
                        <input autoFocus value={q} onChange={(e) => setQ(e.target.value)}
                            placeholder="Rechercher un produit..."
                            className="w-full h-8 pl-7 pr-2 border rounded text-xs outline-none focus:border-orange-400"
                            data-testid={`${testId}-search`} />
                    </div>
                </div>
                <div className="max-h-60 overflow-y-auto overscroll-contain" style={{ touchAction: 'pan-y', WebkitOverflowScrolling: 'touch' }}>
                    {!hasFromSite ? (
                        <div className="py-4 text-center text-xs text-slate-400 italic px-3">Sélectionnez d'abord le site de départ</div>
                    ) : filtered.length === 0 ? (
                        <div className="py-4 text-center text-xs text-slate-400 italic px-3">Aucun produit en stock sur ce site</div>
                    ) : filtered.map(([cat, list]) => (
                        <div key={cat}>
                            <div className="px-2 py-1 text-[10px] font-bold uppercase tracking-wider text-slate-400 bg-slate-50 sticky top-0">{cat}</div>
                            {list.map(p => (
                                <button key={p.id} type="button"
                                    onClick={() => { onChange(p.id); setOpen(false); }}
                                    className={`w-full text-left px-2 py-1.5 text-xs hover:bg-orange-50 flex items-center justify-between gap-2 ${p.id === value ? 'bg-orange-50 font-semibold' : ''}`}
                                    data-testid={`${testId}-opt-${p.id}`}>
                                    <span className="truncate">{p.name} ({p.unit})</span>
                                    {stock[p.id] && <span className="text-[10px] text-emerald-600 flex-shrink-0 tabular-nums">stock: {stock[p.id].quantity} {stock[p.id].unit}</span>}
                                </button>
                            ))}
                        </div>
                    ))}
                </div>
            </PopoverContent>
        </Popover>
    );
}

// Conventions alignees avec OrdersTab.jsx + backend transfers.py::_get_company
const getCompany = (siteName) => {
    if (!siteName) return null;
    const name = siteName.trim();
    if (name.startsWith('FE ')) return 'Fuji Express';
    if (['Concept Lab', 'My Food', 'Boucherie', 'La Boucherie'].includes(name)) return name;
    return null;
};

const isInterCompanyTransfer = (transfer) => {
    const fromCompany = getCompany(transfer.from_site_name);
    if (!fromCompany) return false;
    return (transfer.destinations || []).some(
        d => getCompany(d.site_name) && getCompany(d.site_name) !== fromCompany
    );
};

const downloadTransferPdf = async (transferId, transferNumber) => {
    try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API}/api/transfers/${transferId}/pdf`, {
            headers: { 'Authorization': `Bearer ${token}` },
        });
        if (!res.ok) {
            const err = await res.json().catch(() => ({}));
            toast.error(err.detail || 'Erreur lors de la generation du PDF');
            return;
        }
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `transfert_${transferNumber || transferId}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
    } catch {
        toast.error('Erreur reseau');
    }
};

export default function TransfersPage({ embedded = false }) {
    const { hasPermission } = useAuth();
    const canCreate = hasPermission('transfers_create');
    const canValidate = hasPermission('transfers_validate');
    const [transfers, setTransfers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [sites, setSites] = useState([]);
    const [suppliers, setSuppliers] = useState([]);
    const [supplierProducts, setSupplierProducts] = useState([]);
    const [industrialProducts, setIndustrialProducts] = useState([]);
    const [statusFilter, setStatusFilter] = useState('all');
    const [searchTerm, setSearchTerm] = useState('');
    // Tri : 'transfer_date' = date effective de transfert (par défaut),
    // 'created_at' = date de saisie. Ordre toujours décroissant (plus récent en haut).
    const [sortBy, setSortBy] = useState('transfer_date');
    // Filtre mois : 'all' | 'YYYY-MM'. S'applique sur la clé de tri active
    // (date effective si sortBy=transfer_date, sinon date de saisie).
    const [monthFilter, setMonthFilter] = useState('all');
    const [expandedId, setExpandedId] = useState(null);

    // Create/Edit dialog
    const [createDialog, setCreateDialog] = useState(false);
    const [editingTransferId, setEditingTransferId] = useState(null);
    const [fromSiteId, setFromSiteId] = useState('');
    const [destinations, setDestinations] = useState([]);
    const [transferNotes, setTransferNotes] = useState('');
    const [transferDate, setTransferDate] = useState('');
    const [creating, setCreating] = useState(false);
    const [availableStock, setAvailableStock] = useState({});
    const [supplierStock, setSupplierStock] = useState({});

    // Validate/Reject
    const [confirmDialog, setConfirmDialog] = useState(null); // { id, action: 'validate' | 'reject' }
    const [transit, setTransit] = useState(null);
    const [transitOpen, setTransitOpen] = useState(false);
    const [showHiddenReceptions, setShowHiddenReceptions] = useState(false);
    const [transitDetail, setTransitDetail] = useState(null); // reception object
    const [sourceReceptionId, setSourceReceptionId] = useState(null);

    const reloadTransit = async (includeHidden = showHiddenReceptions) => {
        try {
            const r = await transfersAPI.siegeReceptions({ include_hidden: includeHidden });
            setTransit(r.data);
        } catch { /* silent */ }
    };

    const toggleHideReception = async (rec) => {
        try {
            await transfersAPI.hideSiegeReception(rec.reception_id, !rec.hidden);
            toast.success(rec.hidden ? 'Réception réaffichée' : 'Réception masquée (toujours comptée dans les ratios FE)');
            reloadTransit();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Erreur');
        }
    };

    // Pré-remplit un transfert avec les quantités restantes de la réception (transfert rattaché)
    const openVentilerReception = (rec) => {
        const items = rec.items.filter(it => it.remaining_qty > 0);
        if (!items.length) return;
        setEditingTransferId(null);
        setFromSiteId(transit.site_id);
        setSourceReceptionId(rec.reception_id);
        setDestinations([{
            site_id: '',
            items: items.map(it => ({
                industrial_product_id: it.industrial_product_id || '',
                supplier_product_id: it.supplier_product_id,
                quantity: it.remaining_qty,
                unit: it.billing_unit || 'piece',
            })),
        }]);
        setTransferNotes(`Ventilation ${rec.number}${rec.document_number ? ` (pièce ${rec.document_number})` : ''}`);
        setTransferDate(new Date().toISOString().split('T')[0]);
        setCreateDialog(true);
    };

    // User role
    const [userRole, setUserRole] = useState('');

    useEffect(() => {
        loadData();
    }, []);

    useEffect(() => {
        if (fromSiteId && transferDate) {
            const token = localStorage.getItem('token');
            fetch(`${API}/api/transfers/stock?site_id=${fromSiteId}&date=${transferDate}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            })
            .then(r => r.json())
            .then(data => {
                setAvailableStock(data.by_product || {});
                setSupplierStock(data.by_supplier_product || {});
            })
            .catch(() => { setAvailableStock({}); setSupplierStock({}); });
        } else {
            setAvailableStock({});
            setSupplierStock({});
        }
    }, [fromSiteId, transferDate]);


    const loadData = async () => {
        try {
            const [transfersRes, sitesRes, suppRes, spRes, ipRes, transitRes] = await Promise.all([
                transfersAPI.getAll(),
                sitesAPI.getAll(),
                suppliersAPI.getAll(),
                supplierProductsAPI.getAll().then(r => r.data),
                industrialProductsAPI.getAll().then(r => r.data),
                transfersAPI.siegeReceptions({ include_hidden: false }).catch(() => ({ data: null })),
            ]);
            setTransfers(transfersRes.data);
            setTransit(transitRes.data);

            // Sort sites
            const order = ['génipa', 'genipa', 'cluny', 'dillon', 'océanis', 'oceanis', 'vauclin', 'siège', 'siege', 'concept', 'my food'];
            const sortedSites = (sitesRes.data || []).sort((a, b) => {
                const nA = (a.name || '').toLowerCase();
                const nB = (b.name || '').toLowerCase();
                const iA = order.findIndex(k => nA.includes(k));
                const iB = order.findIndex(k => nB.includes(k));
                return (iA >= 0 ? iA : 999) - (iB >= 0 ? iB : 999);
            });
            setSites(sortedSites);
            setSuppliers(suppRes.data);
            setSupplierProducts(spRes);
            setIndustrialProducts(ipRes);

            // Get user role
            const userData = JSON.parse(localStorage.getItem('user') || '{}');
            setUserRole(userData.role || '');
        } catch {
            toast.error('Erreur de chargement');
        } finally {
            setLoading(false);
        }
    };

    const filteredTransfers = useMemo(() => {
        let list = transfers;
        if (statusFilter !== 'all') list = list.filter(t => t.status === statusFilter);
        if (searchTerm) {
            list = list.filter(t =>
                matchesSearch(t.transfer_number, searchTerm) ||
                matchesSearch(t.from_site_name, searchTerm) ||
                t.destinations?.some(d => matchesSearch(d.site_name, searchTerm))
            );
        }
        // Filtre mois : compare "YYYY-MM" extrait de la clé de tri active.
        if (monthFilter !== 'all') {
            list = list.filter(t => {
                const raw = sortBy === 'created_at'
                    ? t.created_at
                    : (t.transfer_date || t.created_at);
                if (!raw) return false;
                // transfer_date est "YYYY-MM-DD", created_at est ISO — dans les deux cas
                // les 7 premiers caractères donnent "YYYY-MM".
                return String(raw).slice(0, 7) === monthFilter;
            });
        }
        // Tri décroissant selon la clé choisie. `transfer_date` peut être absent → fallback sur created_at.
        const getKey = (t) => {
            if (sortBy === 'created_at') return new Date(t.created_at).getTime();
            return new Date(t.transfer_date || t.created_at).getTime();
        };
        list = [...list].sort((a, b) => getKey(b) - getKey(a));
        return list;
    }, [transfers, statusFilter, searchTerm, sortBy, monthFilter]);

    // Génère les raccourcis de mois : de janvier de l'année en cours jusqu'au mois courant.
    // Ordonnés du plus récent au plus ancien pour rester proche de la liste triée.
    const monthShortcuts = useMemo(() => {
        const now = new Date();
        const year = now.getFullYear();
        const currentMonth = now.getMonth(); // 0-indexed
        const labels = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin', 'Juil', 'Août', 'Sep', 'Oct', 'Nov', 'Déc'];
        const list = [];
        for (let m = currentMonth; m >= 0; m--) {
            const value = `${year}-${String(m + 1).padStart(2, '0')}`;
            list.push({ value, label: labels[m] });
        }
        return list;
    }, []);

    // ============ CREATE TRANSFER ============

    // Pre-fill a transfer with the whole Siège residual (user then picks destinations)
    const openCreateDialog = () => {
        setSourceReceptionId(null);
        setEditingTransferId(null);
        setFromSiteId('');
        setDestinations([{ site_id: '', items: [{ industrial_product_id: '', supplier_product_id: '', quantity: 1, unit: 'piece' }] }]);
        setTransferNotes('');
        setTransferDate(new Date().toISOString().split('T')[0]);
        setCreateDialog(true);
    };

    const openEditDialog = (transfer) => {
        setEditingTransferId(transfer.id);
        setFromSiteId(transfer.from_site_id);
        setDestinations(transfer.destinations.map(d => ({
            site_id: d.site_id,
            items: d.items.map(item => ({
                supplier_product_id: item.supplier_product_id || '',
                industrial_product_id: item.industrial_product_id || '',
                quantity: item.quantity,
                unit: item.unit || 'piece',
            }))
        })));
        setTransferNotes(transfer.notes || '');
        setTransferDate(transfer.transfer_date || new Date(transfer.created_at).toISOString().split('T')[0]);
        setCreateDialog(true);
    };

    const addDestination = () => {
        setDestinations(prev => [...prev, { site_id: '', items: [{ supplier_product_id: '', industrial_product_id: '', quantity: 1, unit: 'piece' }] }]);
    };

    const removeDestination = (idx) => {
        setDestinations(prev => prev.filter((_, i) => i !== idx));
    };

    const updateDestination = (idx, field, value) => {
        setDestinations(prev => prev.map((d, i) => i === idx ? { ...d, [field]: value } : d));
    };

    const addItem = (destIdx) => {
        setDestinations(prev => prev.map((d, i) =>
            i === destIdx ? { ...d, items: [...d.items, { supplier_product_id: '', industrial_product_id: '', quantity: 1, unit: 'piece' }] } : d
        ));
    };

    const removeItem = (destIdx, itemIdx) => {
        setDestinations(prev => prev.map((d, i) =>
            i === destIdx ? { ...d, items: d.items.filter((_, j) => j !== itemIdx) } : d
        ));
    };

    const updateItem = (destIdx, itemIdx, field, value) => {
        setDestinations(prev => prev.map((d, i) =>
            i === destIdx ? {
                ...d,
                items: d.items.map((item, j) => {
                    if (j !== itemIdx) return item;
                    const updated = { ...item, [field]: value };
                    if (field === 'industrial_product_id' && value) {
                        const ip = industrialProducts.find(p => p.id === value);
                        updated.unit = ip?.unit || 'piece';
                        // Filter suppliers with stock
                        const sps = supplierProducts.filter(sp => sp.industrial_product_id === value);
                        const spsWithStock = Object.keys(supplierStock).length > 0
                            ? sps.filter(sp => supplierStock[sp.id]?.quantity > 0)
                            : sps;
                        if (spsWithStock.length === 1) {
                            updated.supplier_product_id = spsWithStock[0].id;
                            updated.unit = spsWithStock[0].invoice_unit || spsWithStock[0].billing_unit || ip?.unit || 'piece';
                        } else {
                            updated.supplier_product_id = '';
                        }
                    }
                    if (field === 'supplier_product_id' && value) {
                        const sp = supplierProducts.find(s => s.id === value);
                        if (sp) {
                            const ip = industrialProducts.find(p => p.id === item.industrial_product_id);
                            updated.unit = sp.invoice_unit || sp.billing_unit || ip?.unit || 'piece';
                        }
                    }
                    return updated;
                })
            } : d
        ));
    };

    const handleCreate = async () => {
        if (!fromSiteId) { toast.error('Selectionnez un site expediteur'); return; }
        if (destinations.length === 0) { toast.error('Ajoutez au moins une destination'); return; }
        for (const dest of destinations) {
            if (!dest.site_id) { toast.error('Selectionnez un site destinataire'); return; }
            if (dest.site_id === fromSiteId) { toast.error('Le site expediteur et destinataire ne peuvent pas etre identiques'); return; }
            if (dest.items.length === 0 || dest.items.some(i => !i.supplier_product_id || i.quantity <= 0)) {
                toast.error('Completez tous les produits avec des quantites valides');
                return;
            }
        }

        setCreating(true);
        try {
            const payload = { from_site_id: fromSiteId, destinations, notes: transferNotes, transfer_date: transferDate, source_reception_id: sourceReceptionId };
            if (editingTransferId) {
                await transfersAPI.update(editingTransferId, payload);
                toast.success('Transfert modifie avec succes');
            } else {
                await transfersAPI.create(payload);
                toast.success('Transfert cree avec succes');
            }
            setCreateDialog(false);
            setSourceReceptionId(null);
            loadData();
        } catch {
            toast.error('Erreur lors de la sauvegarde');
        } finally {
            setCreating(false);
        }
    };

    // ============ VALIDATE / REJECT ============

    const handleConfirmAction = async () => {
        if (!confirmDialog) return;
        try {
            if (confirmDialog.action === 'validate') {
                await transfersAPI.validate(confirmDialog.id);
                toast.success('Transfert valide - mouvements de stock crees');
            } else if (confirmDialog.action === 'reject') {
                await transfersAPI.reject(confirmDialog.id, '');
                toast.success('Transfert rejete');
            } else if (confirmDialog.action === 'delete') {
                await transfersAPI.delete(confirmDialog.id);
                toast.success('Transfert supprime');
            }
            loadData();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Erreur');
        } finally {
            setConfirmDialog(null);
        }
    };

    const handleDelete = async (id) => {
        setConfirmDialog({ id, action: 'delete' });
    };

    // Products indexed by industrial product, with supplier options
    const industrialWithSuppliers = useMemo(() => {
        return industrialProducts.map(ip => {
            const sps = supplierProducts.filter(sp => sp.industrial_product_id === ip.id);
            return {
                ...ip,
                supplierOptions: sps.map(sp => ({
                    ...sp,
                    supplierName: suppliers.find(s => s.id === sp.supplier_id)?.name || '',
                })),
            };
        }).filter(ip => ip.supplierOptions.length > 0)
          .sort((a, b) => {
              const catA = a.category || 'Sans catégorie';
              const catB = b.category || 'Sans catégorie';
              if (catA !== catB) return catA.localeCompare(catB);
              return a.name.localeCompare(b.name);
          });
    }, [industrialProducts, supplierProducts, suppliers]);

    const productsByCategory = useMemo(() => {
        // Uniquement les produits existant en stock sur le site de départ
        if (!fromSiteId) return [];
        const groups = {};
        industrialWithSuppliers.forEach(ip => {
            const stock = availableStock[ip.id];
            if (!stock || stock.quantity <= 0) return;
            const cat = ip.category || 'Sans catégorie';
            if (!groups[cat]) groups[cat] = [];
            groups[cat].push(ip);
        });
        return Object.entries(groups).sort((a, b) => a[0].localeCompare(b[0]));
    }, [industrialWithSuppliers, availableStock, fromSiteId]);

    // Get price for an item (adjusted for transfer unit)
    const getItemPrice = (item) => {
        if (!item.supplier_product_id) return 0;
        const sp = supplierProducts.find(s => s.id === item.supplier_product_id);
        if (!sp) return 0;
        const pricePerBaseUnit = sp.price_per_unit || 0;
        const invoiceUnit = sp.invoice_unit || sp.billing_unit || '';
        const capacityValue = sp.capacity_value || 1;
        // If transferring in invoice unit (Sachet, Carton), multiply by capacity
        if (item.unit && item.unit === invoiceUnit) {
            return pricePerBaseUnit * capacityValue;
        }
        return pricePerBaseUnit;
    };

    // Get available transfer units for an item
    const getTransferUnits = (item) => {
        const ip = industrialProducts.find(p => p.id === item.industrial_product_id);
        const sp = item.supplier_product_id ? supplierProducts.find(s => s.id === item.supplier_product_id) : null;
        const baseUnit = ip?.unit || 'piece';
        const invoiceUnit = sp?.invoice_unit || sp?.billing_unit || '';
        const units = [baseUnit];
        if (invoiceUnit && invoiceUnit !== baseUnit) {
            units.push(invoiceUnit);
        }
        return units;
    };

    // Get total value for a destination
    const getDestValue = (dest) => {
        return dest.items.reduce((sum, item) => sum + (getItemPrice(item) * (item.quantity || 0)), 0);
    };

    // Get total transfer value
    const getTransferValue = () => {
        return destinations.reduce((sum, dest) => sum + getDestValue(dest), 0);
    };

    const isAdmin = userRole === 'admin' || canValidate;

    if (loading) return <div className="flex justify-center py-20"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500" /></div>;

    return (
        <div className="space-y-4 animate-in fade-in duration-300" data-testid="transfers-page">
            {/* Header */}
            <div className="flex items-center justify-between">
                {!embedded && (
                <div>
                    <h1 className="text-xl font-bold text-slate-900 flex items-center gap-2">
                        <ArrowRightLeft className="w-6 h-6 text-orange-500" /> Transferts inter-sites
                    </h1>
                    <p className="text-sm text-slate-500">Gestion des transferts de produits entre sites</p>
                </div>
                )}
                {embedded && <div />}
                {canCreate && (
                    <Button onClick={openCreateDialog} className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="new-transfer-btn">
                        <Plus className="w-4 h-4 mr-1" /> Nouveau transfert
                    </Button>
                )}
            </div>

            {/* Transit FE Siège : suivi de ventilation par réception */}
            {transit && (transit.receptions?.length > 0 || transit.totals?.hidden_count > 0) && (
                <div className="border border-amber-200 bg-amber-50/50 rounded-xl overflow-hidden" data-testid="siege-transit-card">
                    <button
                        type="button"
                        onClick={() => setTransitOpen(v => !v)}
                        className="w-full flex flex-wrap items-center gap-3 px-4 py-3 text-left"
                        data-testid="siege-transit-toggle"
                    >
                        <Warehouse className="w-5 h-5 text-amber-600 shrink-0" />
                        <span className="font-semibold text-slate-900">Transit FE Siège</span>
                        <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100" data-testid="siege-transit-badge">
                            {transit.totals.count_unsolded} réception(s) à ventiler — {formatCurrency(transit.totals.pending_value)}
                        </Badge>
                        <span className="text-xs text-slate-500 hidden sm:inline">Chaque réception du Siège doit être ventilée vers les 5 sites.</span>
                        <span className="ml-auto text-slate-400">{transitOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}</span>
                    </button>
                    {transitOpen && (
                        <div className="px-4 pb-4 space-y-3">
                            <div className="max-h-96 overflow-auto border rounded-lg bg-white">
                                <table className="w-full text-xs">
                                    <thead className="bg-slate-50 text-slate-500 sticky top-0 z-10">
                                        <tr>
                                            <th className="text-left px-3 py-2">Date</th>
                                            <th className="text-left px-3 py-2">Réception</th>
                                            <th className="text-left px-3 py-2">Fournisseur</th>
                                            <th className="text-left px-3 py-2">N° pièce</th>
                                            <th className="text-right px-3 py-2">Valeur</th>
                                            <th className="text-left px-3 py-2 w-40">Ventilation</th>
                                            <th className="text-right px-3 py-2">Âge</th>
                                            <th className="text-right px-3 py-2">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {transit.receptions.map(rec => (
                                            <tr key={rec.reception_id}
                                                className={`border-t cursor-pointer hover:bg-amber-50 ${rec.hidden ? 'opacity-50' : ''}`}
                                                onClick={() => setTransitDetail(rec)}
                                                title="Voir le détail des produits"
                                                data-testid={`transit-rec-${rec.reception_id}`}>
                                                <td className="px-3 py-1.5 tabular-nums">{rec.date ? new Date(rec.date + 'T00:00:00').toLocaleDateString('fr-FR') : '—'}</td>
                                                <td className="px-3 py-1.5 font-mono font-medium">{rec.number}</td>
                                                <td className="px-3 py-1.5 text-slate-500">{rec.supplier_name}</td>
                                                <td className="px-3 py-1.5 font-mono text-slate-500">{rec.document_number || '—'}</td>
                                                <td className="px-3 py-1.5 text-right tabular-nums font-semibold">{formatCurrency(rec.total_value)}</td>
                                                <td className="px-3 py-1.5">
                                                    {rec.status === 'done' ? (
                                                        <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">Soldée</Badge>
                                                    ) : (
                                                        <div className="flex items-center gap-2">
                                                            <div className="w-16 h-1.5 rounded bg-slate-200 overflow-hidden">
                                                                <div className={`h-full ${rec.status === 'partial' ? 'bg-amber-500' : 'bg-red-400'}`} style={{ width: `${Math.max(rec.progress_pct, 3)}%` }} />
                                                            </div>
                                                            <span className={`font-semibold ${rec.status === 'partial' ? 'text-amber-600' : 'text-red-600'}`}>
                                                                {rec.status === 'partial' ? `${rec.progress_pct} %` : 'À ventiler'}
                                                            </span>
                                                        </div>
                                                    )}
                                                </td>
                                                <td className="px-3 py-1.5 text-right">
                                                    {rec.status === 'done' || rec.age_days == null ? <span className="text-slate-300">—</span> : (
                                                        <span className={`font-semibold ${rec.age_days > 1 ? 'text-red-600' : 'text-amber-600'}`}>{rec.age_days} j</span>
                                                    )}
                                                </td>
                                                <td className="px-3 py-1.5 text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                                                    {canCreate && rec.status !== 'done' && (
                                                        <Button size="sm" variant="outline" className="h-6 px-2 text-[11px] border-amber-300 text-amber-700 hover:bg-amber-100 mr-1"
                                                            onClick={() => openVentilerReception(rec)}
                                                            data-testid={`ventiler-${rec.reception_id}`}>
                                                            Ventiler
                                                        </Button>
                                                    )}
                                                    <Button size="sm" variant="ghost" className="h-6 px-2 text-[11px] text-slate-400 hover:text-slate-700"
                                                        onClick={() => toggleHideReception(rec)}
                                                        title={rec.hidden ? 'Réafficher' : 'Masquer (toujours comptée dans les ratios FE)'}
                                                        data-testid={`hide-rec-${rec.reception_id}`}>
                                                        {rec.hidden ? 'Réafficher' : 'Masquer'}
                                                    </Button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            <div className="flex flex-wrap items-center justify-between gap-2">
                                <p className="text-[11px] text-slate-500">
                                    Cliquez sur une réception pour voir le restant par produit. « Ventiler » pré-remplit un transfert rattaché à la réception.
                                    Les transferts historiques sont imputés à la réception la plus proche précédant le transfert (jour même ou veille).
                                </p>
                                <label className="flex items-center gap-1.5 text-[11px] text-slate-500 cursor-pointer">
                                    <input type="checkbox" checked={showHiddenReceptions}
                                        onChange={(e) => { setShowHiddenReceptions(e.target.checked); reloadTransit(e.target.checked); }}
                                        data-testid="show-hidden-receptions" />
                                    Afficher les masquées ({transit.totals.hidden_count})
                                </label>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Détail : produits de la réception cliquée */}
            <Dialog open={!!transitDetail} onOpenChange={(v) => !v && setTransitDetail(null)}>
                <DialogContent className="sm:max-w-3xl sm:overflow-x-hidden">
                    <DialogHeader>
                        <DialogTitle className="text-base">
                            {transitDetail?.number} — {transitDetail?.supplier_name}
                        </DialogTitle>
                        <DialogDescription className="text-xs">
                            {transitDetail?.date && new Date(transitDetail.date + 'T00:00:00').toLocaleDateString('fr-FR')}
                            {transitDetail?.document_number ? ` · Pièce ${transitDetail.document_number}` : ''} · {formatCurrency(transitDetail?.total_value || 0)}
                            {' · '}Restant : <span className="font-semibold">{formatCurrency(transitDetail?.remaining_value || 0)}</span>
                        </DialogDescription>
                    </DialogHeader>
                    <div className="max-h-72 overflow-auto border rounded-lg">
                        <table className="w-full text-xs" data-testid="transit-detail-table">
                            <thead className="bg-slate-50 text-slate-500 sticky top-0">
                                <tr>
                                    <th className="text-left px-3 py-2">Produit</th>
                                    <th className="text-right px-3 py-2">Reçu</th>
                                    <th className="text-right px-3 py-2">Transféré</th>
                                    <th className="text-right px-3 py-2">Restant</th>
                                    <th className="text-right px-3 py-2">Valeur rest.</th>
                                </tr>
                            </thead>
                            <tbody>
                                {(transitDetail?.items || []).map((it, i) => (
                                    <tr key={i} className="border-t" data-testid={`transit-detail-item-${i}`}>
                                        <td className="px-3 py-1.5 font-medium">{it.product_name}</td>
                                        <td className="px-3 py-1.5 text-right tabular-nums">{it.received_qty} {it.billing_unit}</td>
                                        <td className="px-3 py-1.5 text-right tabular-nums">{it.transferred_qty}</td>
                                        <td className={`px-3 py-1.5 text-right tabular-nums font-semibold ${it.remaining_qty > 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                                            {it.remaining_qty}
                                        </td>
                                        <td className="px-3 py-1.5 text-right tabular-nums">{formatCurrency(it.remaining_value)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    {(transitDetail?.transfers || []).length > 0 && (
                        <div className="min-w-0">
                            <p className="text-xs font-semibold text-slate-600 mb-1.5">Ventilations effectuées ({transitDetail.transfers.length})</p>
                            <div className="max-h-48 overflow-auto border rounded-lg min-w-0">
                                <table className="w-full text-xs" data-testid="transit-ventilations-table">
                                    <thead className="bg-slate-50 text-slate-500 sticky top-0">
                                        <tr>
                                            <th className="text-left px-3 py-2">Date</th>
                                            <th className="text-left px-3 py-2">Transfert</th>
                                            <th className="text-left px-3 py-2">Destination</th>
                                            <th className="text-left px-3 py-2">Produit</th>
                                            <th className="text-right px-3 py-2">Qté</th>
                                            <th className="text-right px-3 py-2">Valeur</th>
                                            <th className="text-left px-3 py-2">Statut</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {transitDetail.transfers.map((tr, i) => (
                                            <tr key={i} className="border-t" data-testid={`transit-ventilation-${i}`}>
                                                <td className="px-3 py-1.5 tabular-nums">{tr.date ? new Date(tr.date + 'T00:00:00').toLocaleDateString('fr-FR') : '—'}</td>
                                                <td className="px-3 py-1.5 font-mono whitespace-nowrap">{tr.transfer_number || '—'}</td>
                                                <td className="px-3 py-1.5 font-medium whitespace-nowrap">{tr.site_name}</td>
                                                <td className="px-3 py-1.5 text-slate-500"><div className="max-w-[170px] truncate" title={tr.product_name}>{tr.product_name}</div></td>
                                                <td className="px-3 py-1.5 text-right tabular-nums whitespace-nowrap">{tr.qty} {tr.billing_unit}</td>
                                                <td className="px-3 py-1.5 text-right tabular-nums">{formatCurrency(tr.value)}</td>
                                                <td className="px-3 py-1.5">
                                                    {tr.status === 'validated' ? (
                                                        <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100 text-[10px] px-1.5 py-0">Validé</Badge>
                                                    ) : (
                                                        <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100 text-[10px] px-1.5 py-0">En attente</Badge>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                    {canCreate && transitDetail?.status !== 'done' && (
                        <Button onClick={() => { const r = transitDetail; setTransitDetail(null); openVentilerReception(r); }}
                            className="bg-amber-500 hover:bg-amber-600 text-white" data-testid="detail-ventiler-btn">
                            <Plus className="w-4 h-4 mr-1" /> Ventiler le restant
                        </Button>
                    )}
                </DialogContent>
            </Dialog>

            {/* Filters */}
            <div className="flex items-center gap-3 flex-wrap">
                <div className="relative flex-1 max-w-xs">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                        placeholder="Rechercher..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-9"
                        data-testid="transfer-search"
                    />
                </div>
                {['all', 'pending', 'validated', 'rejected'].map(s => (
                    <Button
                        key={s}
                        variant="outline"
                        size="sm"
                        onClick={() => setStatusFilter(s)}
                        className={statusFilter === s ? 'border-orange-400 text-orange-600 bg-orange-50' : ''}
                        data-testid={`filter-${s}`}
                    >
                        {s === 'all' ? 'Tous' : STATUS_CONFIG[s]?.label}
                        {s !== 'all' && (
                            <span className="ml-1 text-xs">({transfers.filter(t => t.status === s).length})</span>
                        )}
                    </Button>
                ))}
                {/* Tri : bascule entre date effective de transfert et date de saisie */}
                <div className="ml-auto flex items-center gap-2">
                    <span className="text-xs text-slate-500">Trier par :</span>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setSortBy(sortBy === 'transfer_date' ? 'created_at' : 'transfer_date')}
                        className="border-slate-300 hover:bg-slate-50"
                        data-testid="transfer-sort-toggle"
                        title="Basculer le tri entre date effective de transfert et date de saisie"
                    >
                        <ArrowUpDown className="w-3.5 h-3.5 mr-1.5 text-slate-500" />
                        {sortBy === 'transfer_date' ? 'Date de transfert' : 'Date de saisie'}
                    </Button>
                </div>
            </div>

            {/* Raccourcis mois : de janvier de l'année en cours jusqu'au mois courant.
                S'applique sur la clé de tri active (date de transfert ou de saisie). */}
            <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs text-slate-500">Mois :</span>
                <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setMonthFilter('all')}
                    className={`h-7 px-3 text-xs ${monthFilter === 'all' ? 'border-orange-400 text-orange-600 bg-orange-50' : ''}`}
                    data-testid="month-filter-all"
                >
                    Tous
                </Button>
                {monthShortcuts.map(m => (
                    <Button
                        key={m.value}
                        variant="outline"
                        size="sm"
                        onClick={() => setMonthFilter(m.value)}
                        className={`h-7 px-3 text-xs ${monthFilter === m.value ? 'border-orange-400 text-orange-600 bg-orange-50' : ''}`}
                        data-testid={`month-filter-${m.value}`}
                    >
                        {m.label}
                    </Button>
                ))}
            </div>

            {/* List */}
            {filteredTransfers.length === 0 ? (
                <Card><CardContent className="py-12 text-center text-slate-400">
                    <ArrowRightLeft className="w-12 h-12 mx-auto mb-3 text-slate-200" />
                    Aucun transfert{statusFilter !== 'all' ? ` avec le statut "${STATUS_CONFIG[statusFilter]?.label}"` : ''}
                </CardContent></Card>
            ) : (
                <div className="space-y-2">
                    {filteredTransfers.map(transfer => {
                        const isExpanded = expandedId === transfer.id;
                        const totalItems = transfer.destinations?.reduce((sum, d) => sum + (d.items?.length || 0), 0) || 0;
                        const totalValue = transfer.destinations?.reduce((sum, d) =>
                            sum + (d.items?.reduce((s, item) => s + ((item.price_per_unit || 0) * (item.quantity || 0)), 0) || 0), 0) || 0;
                        return (
                            <Card key={transfer.id} className="overflow-hidden" data-testid={`transfer-${transfer.id}`}>
                                <div
                                    className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-slate-50 transition-colors"
                                    onClick={() => setExpandedId(isExpanded ? null : transfer.id)}
                                >
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <span className="font-mono text-sm font-semibold text-slate-700">{transfer.transfer_number}</span>
                                            <StatusBadge status={transfer.status} />
                                        </div>
                                        <div className="flex items-center gap-2 mt-1 text-xs text-slate-500">
                                            <MapPin className="w-3 h-3" />
                                            <span className="font-medium text-slate-700">{transfer.from_site_name}</span>
                                            <ArrowRight className="w-3 h-3 text-orange-400" />
                                            <span>
                                                {transfer.destinations?.map(d => d.site_name).join(', ')}
                                            </span>
                                            <span className="text-slate-300">|</span>
                                            <Package className="w-3 h-3" />
                                            <span>{totalItems} produit{totalItems > 1 ? 's' : ''}</span>
                                            <span className="text-slate-300">|</span>
                                            <span>{new Date(transfer.transfer_date || transfer.created_at).toLocaleDateString('fr-FR')}</span>
                                            {totalValue > 0 && (
                                                <>
                                                    <span className="text-slate-300">|</span>
                                                    <span className="font-semibold text-emerald-600">{totalValue.toFixed(2)} €</span>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2 flex-shrink-0">
                                        {isInterCompanyTransfer(transfer) && (
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                className="h-7 px-2 border-orange-300 text-orange-600 hover:bg-orange-50"
                                                onClick={(e) => { e.stopPropagation(); downloadTransferPdf(transfer.id, transfer.transfer_number); }}
                                                data-testid={`pdf-${transfer.id}`}
                                                title="Telecharger le bon de transfert inter-entreprise"
                                            >
                                                <FileText className="w-3.5 h-3.5 mr-1" /> PDF
                                            </Button>
                                        )}
                                        {isInterCompanyTransfer(transfer) && (
                                            <PrintButton className="h-7 px-2 border-orange-300 text-orange-600 hover:bg-orange-50"
                                                iconClass="w-3.5 h-3.5" testId={`print-${transfer.id}`}
                                                url={() => `${API}/api/transfers/${transfer.id}/pdf`} />
                                        )}
                                        {transfer.status !== 'rejected' && (
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                className="h-7 px-2"
                                                onClick={(e) => { e.stopPropagation(); openEditDialog(transfer); }}
                                                data-testid={`edit-${transfer.id}`}
                                            >
                                                <Pencil className="w-3.5 h-3.5 mr-1" /> Modifier
                                            </Button>
                                        )}
                                        {isAdmin && transfer.status === 'pending' && (
                                            <>
                                                <Button
                                                    size="sm"
                                                    className="bg-emerald-500 hover:bg-emerald-600 text-white h-7 px-2"
                                                    onClick={(e) => { e.stopPropagation(); setConfirmDialog({ id: transfer.id, action: 'validate' }); }}
                                                    data-testid={`validate-${transfer.id}`}
                                                >
                                                    <Check className="w-3.5 h-3.5 mr-1" /> Valider
                                                </Button>
                                                <Button
                                                    size="sm"
                                                    variant="outline"
                                                    className="border-red-300 text-red-600 hover:bg-red-50 h-7 px-2"
                                                    onClick={(e) => { e.stopPropagation(); setConfirmDialog({ id: transfer.id, action: 'reject' }); }}
                                                    data-testid={`reject-${transfer.id}`}
                                                >
                                                    <X className="w-3.5 h-3.5 mr-1" /> Rejeter
                                                </Button>
                                            </>
                                        )}
                                        {transfer.status !== 'rejected' && (
                                            <Button
                                                size="sm" variant="ghost"
                                                className="text-red-400 hover:text-red-600 h-7 w-7 p-0"
                                                onClick={(e) => { e.stopPropagation(); setConfirmDialog({ id: transfer.id, action: 'delete' }); }}
                                            >
                                                <Trash2 className="w-3.5 h-3.5" />
                                            </Button>
                                        )}
                                        {isExpanded ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
                                    </div>
                                </div>
                                {isExpanded && (
                                    <div className="border-t bg-slate-50 px-4 py-3 space-y-3">
                                        {transfer.notes && (
                                            <p className="text-xs text-slate-500 italic">Note : {transfer.notes}</p>
                                        )}
                                        {transfer.destinations?.map((dest, dIdx) => {
                                            const destTotal = dest.items?.reduce((s, item) => s + ((item.price_per_unit || 0) * (item.quantity || 0)), 0) || 0;
                                            return (
                                            <div key={dIdx}>
                                                <div className="flex items-center gap-1.5 mb-1.5">
                                                    <Truck className="w-3.5 h-3.5 text-orange-500" />
                                                    <span className="text-xs font-semibold text-slate-600">Vers {dest.site_name}</span>
                                                </div>
                                                <div className="bg-white rounded-lg border overflow-x-auto">
                                                    <table className="w-full text-xs">
                                                        <thead><tr className="bg-slate-100 text-slate-500">
                                                            <th className="text-left px-3 py-1.5 whitespace-nowrap">Produit industriel</th>
                                                            <th className="text-left px-3 py-1.5 whitespace-nowrap">Produit fournisseur</th>
                                                            <th className="text-left px-3 py-1.5">Fournisseur</th>
                                                            <th className="text-left px-3 py-1.5">Marque</th>
                                                            <th className="text-right px-3 py-1.5">Qte</th>
                                                            <th className="text-left px-3 py-1.5">Unite</th>
                                                            <th className="text-right px-3 py-1.5">P.U.</th>
                                                            <th className="text-right px-3 py-1.5">Valeur</th>
                                                        </tr></thead>
                                                        <tbody>
                                                            {dest.items?.map((item, iIdx) => {
                                                                const lineVal = (item.price_per_unit || 0) * (item.quantity || 0);
                                                                return (
                                                                    <tr key={iIdx} className="border-t">
                                                                        <td className="px-3 py-1.5 font-medium text-slate-700">{item.product_name || 'Inconnu'}</td>
                                                                        <td className="px-3 py-1.5 text-slate-600">{item.supplier_product_name || '-'}</td>
                                                                        <td className="px-3 py-1.5 text-slate-500">{item.supplier_name || '-'}</td>
                                                                        <td className="px-3 py-1.5 text-slate-400 italic">{item.brand || '-'}</td>
                                                                        <td className="px-3 py-1.5 text-right font-semibold text-slate-700">{item.quantity}</td>
                                                                        <td className="px-3 py-1.5 text-slate-500">{item.unit}</td>
                                                                        <td className="px-3 py-1.5 text-right text-slate-500">{item.price_per_unit ? `${item.price_per_unit.toFixed(2)} €` : '-'}</td>
                                                                        <td className="px-3 py-1.5 text-right font-semibold text-emerald-600">{lineVal > 0 ? `${lineVal.toFixed(2)} €` : '-'}</td>
                                                                    </tr>
                                                                );
                                                            })}
                                                        </tbody>
                                                        {destTotal > 0 && (
                                                            <tfoot>
                                                                <tr className="border-t bg-slate-50">
                                                                    <td colSpan={7} className="px-3 py-1.5 text-right text-xs font-medium text-slate-600">Sous-total</td>
                                                                    <td className="px-3 py-1.5 text-right text-xs font-bold text-emerald-700">{destTotal.toFixed(2)} €</td>
                                                                </tr>
                                                            </tfoot>
                                                        )}
                                                    </table>
                                                </div>
                                            </div>
                                            );
                                        })}
                                        {transfer.validated_at && (
                                            <p className="text-[10px] text-slate-400">
                                                {transfer.status === 'validated' ? 'Valide' : 'Rejete'} le {new Date(transfer.validated_at).toLocaleDateString('fr-FR')}
                                                {transfer.rejected_reason && ` — Raison : ${transfer.rejected_reason}`}
                                            </p>
                                        )}
                                    </div>
                                )}
                            </Card>
                        );
                    })}
                </div>
            )}

            {/* Create Dialog */}
            <Dialog open={createDialog} onOpenChange={setCreateDialog}>
                <DialogContent className="max-w-[95vw] w-full h-[100dvh] sm:h-auto sm:max-h-[90vh] inset-0 sm:inset-auto sm:top-[50%] sm:left-[50%] sm:translate-x-[-50%] sm:translate-y-[-50%] translate-x-0 translate-y-0 flex flex-col p-4 sm:p-6 rounded-none sm:rounded-lg" data-testid="create-transfer-dialog">
                    <DialogHeader className="flex-shrink-0">
                        <DialogTitle>{editingTransferId ? 'Modifier le transfert' : 'Nouveau transfert'}</DialogTitle>
                        <DialogDescription>
                            {editingTransferId ? 'Modifiez les details du transfert avant validation' : 'Creer un transfert de produits entre sites'}
                        </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-4 overflow-y-auto flex-1 min-h-0 pr-1">
                        {/* Depart + Destination + Date - responsive */}
                        <div className="flex flex-col md:flex-row md:items-end gap-3">
                            <div className="flex-1">
                                <Label className="text-sm font-medium">Depart</Label>
                                <select
                                    value={fromSiteId}
                                    onChange={(e) => setFromSiteId(e.target.value)}
                                    className="w-full mt-1 h-9 px-3 border rounded-md text-sm bg-white"
                                    data-testid="from-site-select"
                                >
                                    <option value="">-- Choisir --</option>
                                    {sites.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                                </select>
                            </div>
                            <ArrowRight className="w-5 h-5 text-orange-400 flex-shrink-0 hidden md:block mb-2" />
                            <div className="flex-1">
                                <Label className="text-sm font-medium">Destination</Label>
                                <select
                                    value={destinations[0]?.site_id || ''}
                                    onChange={(e) => updateDestination(0, 'site_id', e.target.value)}
                                    className="w-full mt-1 h-9 px-3 border rounded-md text-sm bg-white"
                                    data-testid="dest-site-0"
                                >
                                    <option value="">-- Choisir --</option>
                                    {sites.filter(s => s.id !== fromSiteId).map(s =>
                                        <option key={s.id} value={s.id}>{s.name}</option>
                                    )}
                                </select>
                            </div>
                            <div className="md:w-40">
                                <Label className="text-sm font-medium">Date</Label>
                                <Input
                                    type="date"
                                    value={transferDate}
                                    onChange={(e) => setTransferDate(e.target.value)}
                                    className="mt-1 h-9"
                                    data-testid="transfer-date"
                                />
                            </div>
                        </div>

                        {/* Produits */}
                        <div className="space-y-3">
                            <Label className="text-sm font-medium">Produits</Label>

                            {destinations[0] && (
                                <Card className="border-orange-200">
                                    <CardContent className="p-3 space-y-3">

                                        {/* Items */}
                                        {destinations[0].items.map((item, iIdx) => {
                                            const sp = item.supplier_product_id ? supplierProducts.find(s => s.id === item.supplier_product_id) : null;
                                            const lineValue = getItemPrice(item) * (item.quantity || 0);
                                            const availableSuppliers = supplierProducts.filter(s => s.industrial_product_id === item.industrial_product_id);
                                            const transferUnits = getTransferUnits(item);
                                            const stockInfo = item.industrial_product_id ? availableStock[item.industrial_product_id] : null;
                                            const ip = item.industrial_product_id ? industrialProducts.find(p => p.id === item.industrial_product_id) : null;
                                            // Calculate transfer qty in base unit for comparison
                                            let transferInBase = item.quantity || 0;
                                            if (sp && item.unit && item.unit !== (ip?.unit || '')) {
                                                transferInBase = (item.quantity || 0) * (sp.capacity_value || 1);
                                            }
                                            const stockExceeded = stockInfo && Math.round(transferInBase * 100) / 100 > Math.round(stockInfo.quantity * 100) / 100;
                                            return (
                                                <div key={iIdx} className={`rounded-lg p-2 space-y-1.5 ${stockExceeded ? 'bg-red-50 border border-red-200' : 'bg-slate-50'}`}>
                                                    {/* Ligne 1 : Produit industriel + bouton supprimer */}
                                                    <div className="flex items-center gap-2">
                                                        <ProductPicker
                                                            value={item.industrial_product_id || ''}
                                                            onChange={(v) => updateItem(0, iIdx, 'industrial_product_id', v)}
                                                            groups={productsByCategory}
                                                            stock={availableStock}
                                                            allProducts={industrialProducts}
                                                            hasFromSite={!!fromSiteId}
                                                            testId={`item-product-0-${iIdx}`}
                                                        />
                                                        <Button size="sm" variant="ghost" className="text-red-400 h-7 w-7 p-0" onClick={() => removeItem(0, iIdx)}>
                                                            <X className="w-3.5 h-3.5" />
                                                        </Button>
                                                    </div>
                                                    {/* Ligne 2 : Fournisseur + Quantité + Unité + Prix */}
                                                    {item.industrial_product_id && availableSuppliers.length > 0 && (
                                                        <div className="flex flex-col md:flex-row md:items-center gap-2 pl-2">
                                                            <div className="flex items-center gap-2 flex-1 min-w-0">
                                                                <Truck className="w-3 h-3 text-slate-400 flex-shrink-0" />
                                                                <select
                                                                    value={item.supplier_product_id || ''}
                                                                    onChange={(e) => updateItem(0, iIdx, 'supplier_product_id', e.target.value)}
                                                                    className="flex-1 h-8 px-2 border rounded text-xs bg-white min-w-0"
                                                                    data-testid={`item-supplier-0-${iIdx}`}
                                                                >
                                                                    <option value="">-- Fournisseur --</option>
                                                                    {availableSuppliers
                                                                        .filter(s => {
                                                                            if (Object.keys(supplierStock).length === 0) return true;
                                                                            const stk = supplierStock[s.id];
                                                                            return stk && stk.quantity > 0;
                                                                        })
                                                                        .map(s => {
                                                                        const supName = suppliers.find(sup => sup.id === s.supplier_id)?.name || '';
                                                                        const spStk = supplierStock[s.id];
                                                                        const stkLabel = spStk ? ` — stock: ${spStk.quantity} ${spStk.unit}` : '';
                                                                        return (
                                                                            <option key={s.id} value={s.id}>
                                                                                {supName} — {s.supplier_name_product}{s.brand ? ` [${s.brand}]` : ''} ({s.price_per_unit?.toFixed(2) || '?'} €/{industrialProducts.find(p => p.id === item.industrial_product_id)?.unit || 'u'}){stkLabel}
                                                                            </option>
                                                                        );
                                                                    })}
                                                                </select>
                                                            </div>
                                                            <div className="flex items-center gap-2 pl-5 md:pl-0">
                                                                <Input
                                                                    type="number"
                                                                    min={0.01}
                                                                    step={0.01}
                                                                    value={item.quantity}
                                                                    onChange={(e) => updateItem(0, iIdx, 'quantity', parseFloat(e.target.value) || 0)}
                                                                    className={`w-20 h-8 text-sm text-center ${stockExceeded ? 'border-red-400' : ''}`}
                                                                    data-testid={`item-qty-0-${iIdx}`}
                                                                />
                                                                {transferUnits.length > 1 ? (
                                                                    <select
                                                                        value={item.unit}
                                                                        onChange={(e) => updateItem(0, iIdx, 'unit', e.target.value)}
                                                                        className="h-8 px-1.5 border rounded text-xs bg-white w-20"
                                                                        data-testid={`item-unit-0-${iIdx}`}
                                                                    >
                                                                        {transferUnits.map(u => (
                                                                            <option key={u} value={u}>{u}</option>
                                                                        ))}
                                                                    </select>
                                                                ) : (
                                                                    <span className="text-xs text-slate-500 w-14 text-center">{item.unit}</span>
                                                                )}
                                                                {lineValue > 0 && (
                                                                    <span className="text-xs font-semibold text-emerald-600 w-16 text-right flex-shrink-0">{lineValue.toFixed(2)} €</span>
                                                                )}
                                                            </div>
                                                        </div>
                                                    )}
                                                    {/* Stock indicator */}
                                                    {item.industrial_product_id && stockInfo && stockExceeded && (
                                                        <div className="flex items-center gap-1.5 pl-2 text-xs text-red-500 font-medium">
                                                            <Package className="w-3 h-3 flex-shrink-0" />
                                                            <span>Stock disponible : {stockInfo.quantity} {stockInfo.unit}{sp?.capacity_value && item.unit && item.unit !== (ip?.unit || '') ? ` (≈ ${(stockInfo.quantity / sp.capacity_value).toFixed(1)} ${item.unit})` : ''} — quantite insuffisante</span>
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                        <Button size="sm" variant="ghost" className="text-orange-500 text-xs" onClick={() => addItem(0)}>
                                            <Plus className="w-3 h-3 mr-1" /> Ajouter un produit
                                        </Button>
                                        {getDestValue(destinations[0]) > 0 && (
                                            <div className="text-right text-xs font-semibold text-slate-600 border-t pt-1.5 mt-1">
                                                Total : <span className="text-emerald-600">{getDestValue(destinations[0]).toFixed(2)} €</span>
                                            </div>
                                        )}
                                    </CardContent>
                                </Card>
                            )}
                        </div>

                        {/* Total value */}
                        {destinations.length > 0 && getTransferValue() > 0 && (
                            <div className="bg-emerald-50 border border-emerald-200 rounded-lg px-4 py-2 flex items-center justify-between">
                                <span className="text-sm font-medium text-slate-700">Valeur totale du transfert</span>
                                <span className="text-lg font-bold text-emerald-700" data-testid="transfer-total-value">{getTransferValue().toFixed(2)} €</span>
                            </div>
                        )}

                        {/* Notes */}
                        <div>
                            <Label className="text-sm font-medium">Notes</Label>
                            <Textarea
                                value={transferNotes}
                                onChange={(e) => setTransferNotes(e.target.value)}
                                placeholder="Notes optionnelles..."
                                className="mt-1"
                                rows={2}
                                data-testid="transfer-notes"
                            />
                        </div>
                    </div>

                    <DialogFooter className="flex-shrink-0 pt-2 border-t">
                        <Button variant="outline" onClick={() => setCreateDialog(false)}>Annuler</Button>
                        <Button onClick={handleCreate} disabled={creating} className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="submit-transfer-btn">
                            {creating ? 'Enregistrement...' : (editingTransferId ? 'Enregistrer' : 'Creer le transfert')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Confirm Dialog */}
            <AlertDialog open={!!confirmDialog} onOpenChange={() => setConfirmDialog(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>
                            {confirmDialog?.action === 'validate' ? 'Valider ce transfert ?' :
                             confirmDialog?.action === 'delete' ? 'Supprimer ce transfert ?' :
                             'Rejeter ce transfert ?'}
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                            {confirmDialog?.action === 'validate'
                                ? 'Les mouvements de stock seront crees automatiquement (sortie du site expediteur, entree aux sites destinataires). Cette action est irreversible.'
                                : confirmDialog?.action === 'delete'
                                ? 'Le transfert sera definitivement supprime. Si ce transfert etait valide, les mouvements de stock associes seront annules.'
                                : 'Le transfert sera marque comme rejete. Aucun mouvement de stock ne sera cree.'
                            }
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Annuler</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={handleConfirmAction}
                            className={confirmDialog?.action === 'validate' ? 'bg-emerald-500 hover:bg-emerald-600' : 'bg-red-500 hover:bg-red-600'}
                        >
                            {confirmDialog?.action === 'validate' ? 'Valider' :
                             confirmDialog?.action === 'delete' ? 'Supprimer' :
                             'Rejeter'}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}

